import { MergeTag, SimpleMergeTag } from 'react-email-editor';
import { IRichContentDocumentEditorPlaceholder } from '../../../models';
import {
	Address2Placeholder,
	AddressPlaceholder,
	BioPlaceholder,
	CityPlaceholder,
	CompanyNamePlaceholder,
	CountryPlaceholder,
	FirstNamePlaceholder,
	FullAddressPlaceholder,
	FullNamePlaceholder,
	JobTitlePlaceholder,
	LastNamePlaceholder,
	MeetingDatePlaceholder,
	MeetingDayPlaceholder,
	MeetingHomepageLinkPlaceholder,
	MeetingLocationPlaceholder,
	MeetingTimePlaceholder,
	MeetingVirtualLocation,
	SenderEmailPlaceholder,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderPhonePlaceholder,
	StatePlaceholder,
	Token,
	ZipCodePlaceholder,
} from '../../../models/Token';

// Frozen because every editor shares these records.
const toMergeTags = (entries: ReadonlyArray<readonly [IRichContentDocumentEditorPlaceholder, string]>) =>
	Object.freeze(
		entries.reduce<Record<string, SimpleMergeTag>>((tags, [placeholder, name]) => {
			// Unlayer escapes HTML in merge tag values, so insert the symbol and convert it on export.
			tags[placeholder.value] = { name, value: placeholder.symbol };
			return tags;
		}, {})
	);

// Most-used first; the picker keeps this order because the editor runs with `mergeTagsConfig.sort` off.
const ContactMergeTags = toMergeTags([
	[FirstNamePlaceholder, 'First Name'],
	[LastNamePlaceholder, 'Last Name'],
	[FullNamePlaceholder, 'Full Name'],
	[CompanyNamePlaceholder, 'Company Name'],
	[JobTitlePlaceholder, 'Job Title'],
	[AddressPlaceholder, 'Address'],
	[Address2Placeholder, 'Address 2'],
	[CityPlaceholder, 'City'],
	[StatePlaceholder, 'State/Province'],
	[ZipCodePlaceholder, 'Zip Code'],
	[CountryPlaceholder, 'Country'],
	[FullAddressPlaceholder, 'Full Address'],
	[BioPlaceholder, 'Bio'],
]);

const SenderMergeTags = toMergeTags([
	[SenderFirstNamePlaceholder, 'First Name'],
	[SenderFullNamePlaceholder, 'Full Name'],
	[SenderEmailPlaceholder, 'Email'],
	[SenderPhonePlaceholder, 'Phone Number'],
]);

const MeetingEntries: ReadonlyArray<readonly [IRichContentDocumentEditorPlaceholder, string]> = [
	[MeetingDatePlaceholder, 'Meeting Date'],
	[MeetingDayPlaceholder, 'Meeting Day'],
	[MeetingTimePlaceholder, 'Meeting Time'],
	[MeetingLocationPlaceholder, 'Meeting Location'],
	[MeetingVirtualLocation, 'Virtual Meeting Link'],
];

const MeetingMergeTags = toMergeTags(MeetingEntries);

const MeetingSchedulerMergeTags = toMergeTags([...MeetingEntries, [MeetingHomepageLinkPlaceholder, 'Meeting Link']]);

export const getNewsletterMergeTags = ({
	customFieldPlaceholders,
	meetingSchedulerEnabled,
}: {
	customFieldPlaceholders: ReadonlyArray<IRichContentDocumentEditorPlaceholder>;
	meetingSchedulerEnabled: boolean;
}): Record<string, MergeTag> => {
	const mergeTags: Record<string, MergeTag> = {
		contact: { name: 'Contact', mergeTags: ContactMergeTags },
	};

	if (customFieldPlaceholders.length > 0) {
		const customFieldMergeTags: Record<string, SimpleMergeTag> = {};
		for (const placeholder of customFieldPlaceholders) {
			// Export converts only the built-in `{{…}}` symbols, so custom fields carry the backend token instead.
			customFieldMergeTags[placeholder.value] = {
				name: placeholder.text,
				value: Token.getPlaceholderHtmlStringValue(placeholder),
			};
		}
		mergeTags.customFields = { name: 'Custom Fields', mergeTags: customFieldMergeTags };
	}

	mergeTags.sender = { name: 'Sender', mergeTags: SenderMergeTags };
	mergeTags.meetings = {
		name: 'Meetings',
		mergeTags: meetingSchedulerEnabled ? MeetingSchedulerMergeTags : MeetingMergeTags,
	};

	return mergeTags;
};
